import { ReactNode, useState } from 'react';
import styled from 'styled-components';
import { Flex } from '../../Flex';
import { Input, InputCss } from './Input';
import { useStyledTheme } from '../../../styling';
import { useFormConfigurationContext } from '../../../..';

const RadioInputTextMessage = styled.label<{ disabled?: boolean, checked?: boolean, hasError: boolean }>`${({
  theme, disabled, hasError,
}) => `
  font-size: ${theme.fontSize[2]};
  line-height: ${theme.fontSize[6]};
  color: ${(() => {
    if (disabled) return theme.colours.neutral[5];
    if (hasError) return theme.textDanger;
    return theme.colours.neutral[2];
  })()};
  width: max-content;
  align-items: center;
  cursor: ${disabled ? 'default' : 'pointer'};
`}`;

export const RadioFieldInput = styled.input<{ checked?: boolean, styles?: string, hasError: boolean, disabled?: boolean }>`
  ${InputCss};
  ${({
    checked, hasError, theme, disabled, styles,
  }) => `
  display: inline-block;
  width: ${theme.fontSize[3]};
  height: ${theme.fontSize[3]};
  -webkit-appearance: none;
  border-radius: 50%;
  margin-right: ${theme.padding[2]};
  margin-left: 0;
  cursor: pointer;
  background: ${theme.colours.neutral[10]};
  border-color: ${theme.colours.neutral[5]};
  padding: ${theme.padding[1]};

  ${checked && `
    border: ${theme.borderWidth[1]} solid ${theme.colours.secondary[1]};
    background: ${theme.colours.secondary[1]};
    box-shadow: inset 0px 0px 0px calc(${theme.borderWidth[2]} + ${theme.borderWidth[1]}) ${theme.textSecondary};
  `};

  ${hasError && `
    border: ${theme.borderWidth[1]} solid ${theme.colours.textDanger};
  `};

  ${disabled && `
    border: ${theme.borderWidth[1]} solid ${theme.colours.neutral[6]};
    background: ${theme.colours.neutral[8]};
    cursor: default;
  `};

  ${disabled && (checked) && `
    border: ${theme.borderWidth[1]} solid ${theme.colours.neutral[6]};
    background: ${theme.colours.neutral[6]};
  `};

  ${disabled && (!checked) && `
    background: ${theme.colours.neutral[8]};
    border: ${theme.borderWidth[1]} solid ${theme.colours.neutral[6]};
  `};

  ${!disabled && `
    &:hover {
      border: ${theme.borderWidth[1]} solid ${theme.colours.secondary[1]};
    }
    &:focus {  
      border: none;
      outline: none;
      box-shadow: 0px 0px 0px 6px ${theme.colours.info[1]};
      outline: 2px solid ${theme.colours.neutral[10]};
    }
  `};

  ${styles || ''};
`}`;

const RadioContainer = styled(Flex) <{ disabled: boolean }>`${({ disabled }) => `
    align-items: flex-start;
    cursor: ${disabled ? 'default' : 'pointer'};
`}`;

type RadioFieldProps = {
  id: string,
  label?: ReactNode,
  value: boolean,
  errors?: string[],
  onChange: any,
  labelStyles?: string,
  styles?: string,
  containerStyles?: string,
  type?: string,
  name?: string,
  visible?: boolean,
  disabled?: boolean,
  onBlur?: () => void,
  touchOverride?: boolean,
  isInline?: boolean,
}

export function RadioInput({
  id,
  label = '',
  value = false,
  errors = [],
  onChange,
  labelStyles = '',
  styles = '',
  containerStyles,
  type = 'radio',
  name,
  visible = true,
  disabled = false,
  onBlur = () => { },
  touchOverride = false,
  isInline = false,
}: RadioFieldProps) {
  const theme = useStyledTheme();
  const [touched, setTouched] = useState(false);
  const hasError = (touched || touchOverride) && !!errors.length;
  const handleSelectChange = () => {
    onChange(!value);
  };

  const onTouched = () => {
    setTouched(true);
    onBlur();
  };

  const { isDisabled } = useFormConfigurationContext(disabled);

  const defaultContainerStyles = 'display:inline-flex; width: auto; justify-content: flex-start; flex-direction: row; align-items: center;';
  const inlineLayoutStyles = `margin-bottom: ${theme.padding[1]}; padding-left: ${theme.padding[6]}; ${defaultContainerStyles}`;
  const layoutStyles = `margin-top: ${theme.padding[1]}; padding-right: ${theme.padding[4]}; ${defaultContainerStyles}`;

  return (
    <>
      {visible && (
        <Input
          id={id}
          hasError={hasError}
          labelStyles={labelStyles}
          containerStyles={containerStyles ?? (isInline ? inlineLayoutStyles : layoutStyles)}
        >
          <RadioContainer id={`${id}-container`} disabled={isDisabled}>
            <RadioFieldInput
              id={id}
              type={type}
              name={name}
              checked={value}
              styles={styles}
              hasError={hasError}
              disabled={isDisabled}
              onChange={() => handleSelectChange()}
              onBlur={onTouched}
            />
            {label && <RadioInputTextMessage htmlFor={id} hasError={hasError} disabled={isDisabled} checked={value}>{label}</RadioInputTextMessage>}
          </RadioContainer>
        </Input>
      )}
    </>
  );
}
